import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined'
import { RootState } from '../../../store'
import { useLocations } from '../../../hooks/locations'
import { useCurrentUser } from '../../../hooks/users'
import { useClimateHazards } from '../../../hooks/analytics'
import usePortfolioHazardRisks from '../../../hooks/climateData/usePortfolioHazardRisks'
import usePortfolioHazardAal from '../../../hooks/climateData/usePortfolioHazardAal'
import { useHazardScenarioPreferences } from '../common/useHazardScenarioPreferences'
import { getClimateHazardViewConfig } from '../common/hazardConfig'
import HazardScenarioPreferenceControl from '../common/HazardScenarioPreferenceControl'
import type { AdaptationView, CanonicalPeriod, EmissionTier } from '../common'
import PortfolioHazardCard from './PortfolioHazardCard'
import PortfolioInfoPopup from './PortfolioInfoPopup'
import PortfolioRiskTable from './PortfolioRiskTable'
import PortfolioAalMetadata from './PortfolioAalMetadata'
import { summarizeCombinedAal, summarizePortfolioRevenue } from './portfolioAal'
import { summarizePortfolioExposure, type PortfolioRiskProperty } from './portfolioRisk'
import siteMap from '../../../siteMap'

type Props = { emissionTier: EmissionTier; selectedPeriod: CanonicalPeriod; view: AdaptationView }

export default function PortfolioOverview({ emissionTier, selectedPeriod, view }: Props) {
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const authUser = useSelector((state: RootState) => state.auth.user)
  const { data: hazards } = useClimateHazards()
  const {
    data: locations,
    isLoading: locationsLoading,
    error: locationsError,
    refetch: retryLocations,
  } = useLocations({ group_id: groupId ?? undefined })
  const { currencyISO } = useCurrentUser()
  const properties: PortfolioRiskProperty[] = locations
  const floodPreferencesConfig = getClimateHazardViewConfig('fld').scenarioPreferences
  const floodPreferences = useHazardScenarioPreferences(authUser, 'fld', floodPreferencesConfig)
  const risks = usePortfolioHazardRisks({
    hazards,
    groupId: groupId ?? undefined,
    propertyIds: properties.map(property => property.id),
    emissionTier,
    selectedPeriod,
    view,
    floodFilters: floodPreferences.filters,
    enabled: !locationsLoading && !locationsError,
  })
  const exposure = summarizePortfolioExposure(properties)
  const annualRevenue = summarizePortfolioRevenue(properties)
  const hazardAals = usePortfolioHazardAal({
    hazards,
    groupId: groupId ?? undefined,
    propertyIds: properties.map(property => property.id),
    emissionTier,
    selectedPeriod,
    view,
    floodFilters: floodPreferences.filters,
    enabled: !locationsLoading && !locationsError,
  })
  const combinedAal = summarizeCombinedAal(
    hazardAals,
    properties.map(property => property.id),
  )
  const [tableView, setTableView] = useState<'risk' | 'aal'>('risk')
  const transitionKey = `${groupId}-${emissionTier}-${selectedPeriod}-${view}`
  const currency = currencyISO || 'GBP'
  const formatMoney = (value: number | null) =>
    value == null
      ? '—'
      : new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency,
          notation: 'compact',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(value)
  const openHazard = useCallback(
    (hazardCode: string) => {
      history.push(siteMap.ClimateAnalytics.getPath(hazardCode, emissionTier, selectedPeriod, view))
    },
    [history, emissionTier, selectedPeriod, view],
  )
  const openProperty = useCallback((id: number) => history.push(siteMap.Property.getPath(id)), [history])
  const openPropertyHazard = useCallback(
    (id: number, hazardCode: string) => {
      history.push(siteMap.PropertyClimateAnalytics.getPath(id, hazardCode, emissionTier, selectedPeriod, view))
    },
    [history, emissionTier, selectedPeriod, view],
  )

  if (locationsError)
    return (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            onClick={() => {
              void retryLocations()
            }}
          >
            Retry
          </Button>
        }
      >
        Unable to load the properties in this portfolio.
      </Alert>
    )

  return (
    <Stack spacing={4} pb={4}>
      <Box component="section" aria-labelledby="portfolio-snapshot-title">
        <Typography
          id="portfolio-snapshot-title"
          variant="h6"
          fontWeight={800}
          mb={2}
          sx={{ letterSpacing: '-0.025em' }}
        >
          Summary
        </Typography>
        <Grid container spacing={2}>
          {[
            {
              title: 'Properties',
              loading: locationsLoading,
              wide: false,
              value: properties.length.toLocaleString(),
              icon: <BusinessOutlinedIcon />,
              metadata: (
                <Typography variant="body2">
                  All properties in the currently selected portfolio or group. Financial and risk values are calculated
                  only from these properties.
                </Typography>
              ),
            },
            {
              title: 'Total Value',
              loading: locationsLoading,
              wide: false,
              value: formatMoney(exposure.value),
              icon: <AccountBalanceOutlinedIcon />,
              metadata: (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    Sum of recorded structure and contents values. Annual revenue is not included. Missing values are
                    excluded, not treated as zero; recorded zero values are included.
                  </Typography>
                  <Typography variant="body2">
                    Contributing properties:{' '}
                    <strong>
                      {exposure.contributors} of {exposure.total}
                    </strong>
                  </Typography>
                  <Typography variant="body2">
                    Structure values: <strong>{exposure.structureContributors} properties</strong>
                  </Typography>
                  <Typography variant="body2">
                    Contents values: <strong>{exposure.contentsContributors} properties</strong>
                  </Typography>
                  {exposure.value != null && (
                    <Typography variant="body2">
                      Exact total value:{' '}
                      <strong>
                        {new Intl.NumberFormat('en-GB', {
                          style: 'currency',
                          currency,
                          maximumFractionDigits: 0,
                        }).format(exposure.value)}
                      </strong>
                    </Typography>
                  )}
                </Stack>
              ),
            },
            {
              title: 'Annual revenue',
              loading: locationsLoading,
              wide: false,
              value: formatMoney(annualRevenue.value),
              icon: <PaymentsOutlinedIcon />,
              metadata: (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    Sum of recorded annual revenue for properties in this portfolio.
                  </Typography>
                  <Typography variant="body2">
                    Contributing properties:{' '}
                    <strong>
                      {annualRevenue.contributors} of {annualRevenue.total}
                    </strong>
                  </Typography>
                  {annualRevenue.value != null && (
                    <Typography variant="body2">
                      Exact annual revenue:{' '}
                      <strong>
                        {new Intl.NumberFormat('en-GB', {
                          style: 'currency',
                          currency,
                          maximumFractionDigits: 0,
                        }).format(annualRevenue.value)}
                      </strong>
                    </Typography>
                  )}
                </Stack>
              ),
            },
            {
              title: 'Combined AAL',
              loading: locationsLoading || combinedAal.loading,
              wide: true,
              value: formatMoney(combinedAal.value),
              icon: <TrendingUpOutlinedIcon />,
              metadata: <PortfolioAalMetadata hazards={hazardAals} summary={combinedAal} currency={currency} />,
            },
          ].map(metric => (
            <Grid item xs={12} sm={6} lg={3} key={metric.title}>
              <Card
                variant="outlined"
                sx={{
                  p: 0,
                  height: '100%',
                  borderRadius: 3,
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.025),
                }}
              >
                <CardContent sx={{ p: 2.5, height: '100%' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ minHeight: 32 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ color: 'primary.main', display: 'flex' }}>{metric.icon}</Box>
                      <Typography variant="subtitle1" fontWeight={800}>
                        {metric.title}
                      </Typography>
                    </Stack>
                    <PortfolioInfoPopup title={metric.title} wide={metric.wide}>
                      {locationsLoading ? (
                        <Typography variant="body2">Portfolio properties are loading…</Typography>
                      ) : (
                        metric.metadata
                      )}
                    </PortfolioInfoPopup>
                  </Stack>
                  {metric.loading ? (
                    <Skeleton width={150} height={60} />
                  ) : (
                    <Typography
                      variant="h3"
                      fontWeight={800}
                      mt={1.5}
                      sx={{ fontVariantNumeric: 'tabular-nums', fontSize: { xs: 40, lg: 36 } }}
                    >
                      {metric.value}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box component="section" aria-labelledby="portfolio-hazard-profile-title">
        <Stack direction="row" alignItems="center" spacing={0.5} mb={2}>
          <Typography
            id="portfolio-hazard-profile-title"
            variant="h6"
            fontWeight={800}
            sx={{ letterSpacing: '-0.025em' }}
          >
            Hazards
          </Typography>
          <PortfolioInfoPopup title="Hazards" wide>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
              Coverage and adaptation settings for the selected emissions pathway and horizon. Adapted mode uses adapted
              results where available, and raw results otherwise. Hazards without adaptation support always use raw
              results.
            </Typography>
            <TableContainer>
              <Table size="small" aria-label="Hazard coverage and adaptations">
                <TableHead>
                  <TableRow>
                    <TableCell>Hazard</TableCell>
                    <TableCell>Score contributors</TableCell>
                    <TableCell>Adaptations</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {risks.map(risk => (
                    <TableRow key={risk.hazard.name}>
                      <TableCell>{risk.hazard.display_name}</TableCell>
                      <TableCell>
                        {risk.loading
                          ? 'Loading…'
                          : risk.error
                          ? 'Load failed'
                          : !risk.periodAvailable
                          ? 'Horizon unavailable'
                          : `${risk.summary.contributors} / ${risk.summary.total}`}
                      </TableCell>
                      <TableCell>
                        {getClimateHazardViewConfig(risk.hazard.name).supportsAdaptations
                          ? risk.adaptationsEnabled
                            ? 'Enabled'
                            : 'Off'
                          : 'Not available'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </PortfolioInfoPopup>
        </Stack>
        {!locationsLoading && properties.length === 0 ? (
          <Alert severity="info">This portfolio has no properties.</Alert>
        ) : null}
        {hazards.length === 0 ? (
          <Alert severity="info">No climate hazards are available.</Alert>
        ) : (
          <Grid container spacing={2}>
            {risks.map(risk => (
              <Grid item xs={12} sm={6} lg={4} key={risk.hazard.name}>
                <PortfolioHazardCard
                  risk={risk}
                  transitionKey={transitionKey}
                  onOpen={() => openHazard(risk.hazard.name)}
                  settings={
                    risk.hazard.name === 'fld' && floodPreferencesConfig ? (
                      <HazardScenarioPreferenceControl
                        hazardCode="fld"
                        hazardLabel={risk.hazard.display_name}
                        config={floodPreferencesConfig}
                        filters={floodPreferences.filters}
                        onSave={floodPreferences.saveFilters}
                      />
                    ) : undefined
                  }
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Box component="section" aria-labelledby="portfolio-property-risk-title">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
          mb={2}
        >
          <Typography
            id="portfolio-property-risk-title"
            variant="h6"
            fontWeight={800}
            sx={{ letterSpacing: '-0.025em' }}
          >
            Properties
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={tableView}
            aria-label="Property table metric"
            onChange={(_, next) => {
              if (next) setTableView(next)
            }}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 2 } }}
          >
            <ToggleButton value="risk" aria-label="Risk scores">
              Risk scores
            </ToggleButton>
            <ToggleButton value="aal" aria-label="Annual losses">
              Annual losses
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Card variant="outlined" sx={{ p: 0, borderRadius: 3 }}>
          <CardContent sx={{ p: 2.5 }}>
            <PortfolioRiskTable
              properties={properties}
              risks={risks}
              hazardAals={hazardAals}
              view={tableView}
              loading={locationsLoading}
              currencyISO={currency}
              onOpenProperty={openProperty}
              onOpenHazard={openPropertyHazard}
            />
          </CardContent>
        </Card>
      </Box>
    </Stack>
  )
}
